import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { API, assetSrc } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { FloppyDisk, Trash, ArrowLeft, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";

const INPUT = "w-full border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";
const LABEL = "text-xs font-bold uppercase tracking-wider text-muted-foreground";

const ImageField = ({ testid, label, value, onChange }) => {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/uploads/image`, fd);
      onChange(data.url);
      toast.success(t.saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.saveFailed);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div data-testid={testid}>
      <div className={`${LABEL} mb-2`}>{label}</div>
      <div className="flex items-start gap-4">
        <div className="flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden border border-border bg-white">
          {value ? <img src={assetSrc(value)} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageIcon size={28} className="text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={`${testid}-file`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-1.5 bg-primary px-3 py-2 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
            <UploadSimple size={15} weight="bold" /> {uploading ? t.uploading : t.uploadImage}
          </button>
          <input data-testid={`${testid}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={t.orUrl} className={INPUT} />
        </div>
      </div>
    </div>
  );
};

export default function BannerEditPage() {
  const { t } = useLang();
  const { bannerId } = useParams();
  const isNew = !bannerId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState({ name: "", image_fr: "", image_en: "", link_url: "", open_new_window: false, status: "active", position: 0, location: "carousel" });

  useEffect(() => {
    if (isNew) return;
    axios.get(`${API}/admin/banners/${bannerId}`)
      .then((r) => setB(r.data))
      .catch(() => toast.error(t.saveFailed))
      .finally(() => setLoading(false));
  }, [bannerId]);

  const set = (k, v) => setB((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    const body = {
      name: b.name || "Bannière", image_fr: b.image_fr || "", image_en: b.image_en || "",
      link_url: b.link_url || "", open_new_window: !!b.open_new_window,
      status: b.status || "active", position: Number(b.position) || 0,
      location: b.location || "carousel",
    };
    try {
      if (isNew) await axios.post(`${API}/admin/banners`, body);
      else await axios.put(`${API}/admin/banners/${bannerId}`, body);
      toast.success(t.saved);
      navigate("/admin/website");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t.confirmDeleteBanner)) return;
    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`);
      toast.success(t.saved);
      navigate("/admin/website");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.saveFailed);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/admin/website")} data-testid="back-to-banners"
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-primary">
          <ArrowLeft size={15} weight="bold" /> {t.banners}
        </button>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
          <h1 className="font-display text-3xl font-black tracking-tighter text-foreground">{isNew ? t.newBanner : t.editBanner}</h1>
          <div className="flex items-center gap-2">
            {!isNew && (
              <button data-testid="delete-banner-button" onClick={remove}
                className="inline-flex items-center gap-2 border border-red-400 px-4 py-3 text-xs font-bold uppercase tracking-wider text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-200">
                <Trash size={15} weight="bold" /> {t.deleteBanner}
              </button>
            )}
            <button data-testid="save-banner-button" onClick={save} disabled={saving || loading}
              className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              <FloppyDisk size={16} weight="bold" /> {t.save}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">{t.loading}</div>
        ) : (
          <div className="space-y-6 border border-border bg-card p-6">
            <div>
              <div className={`${LABEL} mb-2`}>{t.bannerName}</div>
              <input data-testid="banner-name" value={b.name} onChange={(e) => set("name", e.target.value)} className={INPUT} />
            </div>
            <div>
              <div className={`${LABEL} mb-2`}>{t.bannerType}</div>
              <input value={t.graphicBanner} disabled className={`${INPUT} opacity-70`} />
            </div>
            <div>
              <div className={`${LABEL} mb-2`}>{t.location}</div>
              <select data-testid="banner-location" value={b.location || "carousel"} onChange={(e) => set("location", e.target.value)} className={INPUT}>
                <option value="carousel">{t.locCarousel}</option>
                <option value="middle_left">{t.locLeft}</option>
                <option value="middle_right">{t.locRight}</option>
              </select>
            </div>

            <ImageField testid="banner-image-fr" label={t.imageFr} value={b.image_fr} onChange={(v) => set("image_fr", v)} />
            <ImageField testid="banner-image-en" label={t.imageEn} value={b.image_en} onChange={(v) => set("image_en", v)} />

            <div>
              <div className={`${LABEL} mb-2`}>{t.linkUrl}</div>
              <input data-testid="banner-link" value={b.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="/shop?category=… ou https://…" className={INPUT} />
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground" data-testid="banner-newwindow-label">
              <input type="checkbox" data-testid="banner-newwindow" checked={!!b.open_new_window} onChange={(e) => set("open_new_window", e.target.checked)} className="h-4 w-4 accent-[color:var(--primary)]" />
              {t.openNewWindow}
            </label>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className={`${LABEL} mb-2`}>{t.status}</div>
                <select data-testid="banner-status" value={b.status} onChange={(e) => set("status", e.target.value)} className={INPUT}>
                  <option value="active">{t.active}</option>
                  <option value="disabled">{t.disabled}</option>
                </select>
              </div>
              <div>
                <div className={`${LABEL} mb-2`}>{t.displayOrder}</div>
                <input data-testid="banner-position" type="number" value={b.position} onChange={(e) => set("position", e.target.value)} className={INPUT} />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
